/**
 * RaceRegistrationStatus Component
 *
 * Displays compact per-event race registration status inline.
 * Each event shows as: "Race #1: Milledgeville ✓" or "Race #1: Milledgeville ✗"
 * Green check for registered, red X for not registered.
 *
 * Requirements: 12.1, 12.2, 12.3, 12.6
 */

import { sortRaceEventsByStartTime, getRegistrationStatus } from '@/utils/raceRegistration';
import type { Event } from '@/api/rest-client';

export interface RaceEvent {
  event_id: string;
  event_name: string;
  event_starttime: string;
}

export interface RaceRegistrationStatusProps {
  raceRegistrations: Record<string, boolean>;
  raceEvents: RaceEvent[];
  className?: string;
}

export default function RaceRegistrationStatus({
  raceRegistrations,
  raceEvents,
  className = 'text-xs text-gray-600',
}: RaceRegistrationStatusProps) {
  if (!raceEvents || raceEvents.length === 0) {
    return null;
  }

  const sorted = sortRaceEventsByStartTime(raceEvents as Event[]);

  return (
    <div className="space-y-1">
      {sorted.map((event) => {
        const registered = getRegistrationStatus(raceRegistrations, event.event_id);
        return (
          <div key={event.event_id} className={`flex items-center gap-1 ${className}`}>
            <span>{event.event_name}</span>
            {registered ? (
              <span className="text-green-600 font-bold" aria-label="Registered">✓</span>
            ) : (
              <span className="text-red-600 font-bold" aria-label="Not registered">✗</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
